import { buildTeaPayload } from './tea';

const MODEL_TYPE = 'equation';

// `ParamSpec.default` is typed `float` on the backend and the serializer
// declares it a FloatField, so boolean params ride the wire as 1 / 0. The
// form's boolean editor is a <select> holding the *strings* 'true' / 'false',
// which parseFloat turns into NaN — a plain `parseFloat(x) || 0` therefore
// collapses every boolean default to false. Branch on the declared type
// instead of guessing from the value.
export function serializeParamDefault(param) {
  if (param?.type === 'boolean') {
    return param.default === true || param.default === 'true' ? 1 : 0;
  }
  const num = parseFloat(param?.default);
  return Number.isFinite(num) ? num : 0;
}

// Emissions outputs are never allocatable — the backend builds them with
// `connectable=not emissions` and allocation only applies to products. This
// is the single place that invariant is enforced, so it holds for whatever
// the user checked, whatever the AI agent wrote back, and every payload.
export function normalizeOutput(io) {
  const emissions = !!io?.emissions;
  return {
    label: io?.label,
    species: io?.species,
    emissions,
    allocatable: emissions ? false : io?.allocatable !== false,
  };
}

export const normalizeInput = io => ({ label: io?.label, species: io?.species });

const normalizeParam = p => ({
  label: p?.label,
  type: p?.type,
  unit: p?.unit,
  default: serializeParamDefault(p),
});

// Wraps a row normalizer so a row copied from a saved module keeps its `name`:
// the balance refers to rows by those names, so they ride along rather than
// being re-derived from labels that may since have changed.
export const withName = normalize => row => (row?.name ? { name: row.name, ...normalize(row) } : normalize(row));

// The IO / param / balance core shared by the validate, test, and create
// endpoints. Keeping one builder means a normalization fix lands on all
// three at once rather than on whichever call site someone remembered.
export function buildModuleData({ label, inputs, outputs, params, balance }) {
  return {
    label,
    inputs: (inputs ?? []).map(withName(normalizeInput)),
    outputs: (outputs ?? []).map(withName(normalizeOutput)),
    params: (params ?? []).map(withName(normalizeParam)),
    balance,
  };
}

// Payload for /validate/ and /test/. Neither endpoint persists anything, so
// both send a placeholder label when the form doesn't have a real one yet.
export function buildPreviewPayload(form, { label } = {}) {
  return {
    model_type: MODEL_TYPE,
    data: buildModuleData({ ...form, label: label?.trim() || 'preview' }),
  };
}

// Append the balance block to the description so the module's formulation is
// visible wherever the description is shown (registry tooltip, node-info
// panel, exports). Markdown fenced block so renderers that support it
// display it as code.
export function buildFullDescription({ description, balance }) {
  const userDescription = (description || '').trim();
  const balanceBlock = (balance || '').trim();
  if (!balanceBlock) return userDescription;
  return `${userDescription ? `${userDescription}\n\n` : ''}**Formulation**\n\n\`\`\`\n${balanceBlock}\n\`\`\``;
}

// Payload for POST /custom_models/ — the preview core plus the fields that
// only matter once the module is persisted.
export function buildCreatePayload(form) {
  return {
    model_type: MODEL_TYPE,
    permissions: form.permissions,
    ...(form.permissions === 'project' && { project_ids: form.project_ids ?? [] }),
    data: {
      ...buildModuleData(form),
      description: buildFullDescription(form),
      tea: buildTeaPayload(form.tea, form.outputs),
    },
  };
}
